import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, IconButton, Paper, Stack,
  TextField, Typography,
} from '@mui/material';
import { BsSend } from 'react-icons/bs';

import axiosInstance from '../../axiosInstance';
import Proposition from './Proposition';

/** Ce que l'agent propose de créer, et le mot qui le dit. */
const ACTIONS = {
  costings_proposed: 'Créer les postes de coût',
  content_proposed: 'Créer les objets de contenu',
  extraction_proposed: 'Enregistrer les données extraites',
  tasks_proposed: 'Créer les tâches',
  letter_proposed: 'Créer le courrier',
  data_proposed: 'Appliquer les données',
};

/**
 * L'assistant du dossier ou d'un document.
 *
 * Chaque envoi est un appel payant : rien ne part sans un geste de l'expert,
 * et l'agent ne crée jamais rien de lui-même — il propose, on confirme.
 */
export default function GedChat({
  documentUnitId = null,
  expertiseId = null,
  sessionId = null,
  onSessionCreated = null,
  quickActions = [],
  emptyHint = 'Posez une question…',
  placeholder = 'Posez une question…',
  hauteur = '60vh',
}) {
  const [messages, setMessages] = useState([]);
  const [saisie, setSaisie] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [application, setApplication] = useState(null);
  const [session, setSession] = useState(sessionId);
  const [recettes, setRecettes] = useState([]);
  const [erreur, setErreur] = useState('');
  const bas = useRef(null);
  const prevenir = useRef(onSessionCreated);
  prevenir.current = onSessionCreated;

  useEffect(() => {
    const contexte = documentUnitId ? 'document' : 'expertise';
    axiosInstance.get('/ged/assistant-skills/', { params: { context: contexte } })
      .then(({ data }) => setRecettes(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [documentUnitId]);

  useEffect(() => {
    if (session) {
      axiosInstance.get(`/ged/chat-sessions/${session}/messages/`)
        .then(({ data }) => setMessages(data))
        .catch(() => {});
      return;
    }
    if (!documentUnitId && !expertiseId) return;
    const corps = documentUnitId
      ? { context_type: 'document', document_unit_id: documentUnitId }
      : { context_type: 'expertise', expertise_id: expertiseId };
    axiosInstance.post('/ged/chat-sessions/', corps)
      .then(({ data }) => {
        setSession(data.id);
        // Passé par une référence : la fonction du parent change à chaque
        // rendu et relancerait la création d'une session à chaque fois.
        if (prevenir.current) prevenir.current(data.id);
      })
      .catch(() => setErreur("La session d'assistance n'a pas pu s'ouvrir."));
  }, [documentUnitId, expertiseId, session]);

  useEffect(() => {
    bas.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const envoyer = useCallback(async (contenu, recetteId = null) => {
    if (!contenu.trim() || !session) return;
    setEnvoi(true);
    setErreur('');
    setSaisie('');

    const provisoire = { id: `tmp-${Date.now()}`, role: 'user',
                         content: contenu };
    setMessages((etat) => [...etat, provisoire]);

    try {
      const { data } = await axiosInstance.post(
        `/ged/chat-sessions/${session}/messages/`,
        recetteId ? { content: contenu, skill_id: recetteId }
                  : { content: contenu });
      setMessages((etat) => [
        ...etat.filter((m) => m.id !== provisoire.id),
        { ...provisoire, id: `${provisoire.id}_envoye` },
        data,
      ]);
    } catch {
      setErreur("Le message n'est pas parti. Il n'a pas été facturé.");
      setMessages((etat) => etat.filter((m) => m.id !== provisoire.id));
    } finally {
      setEnvoi(false);
    }
  }, [session]);

  const appliquer = async (message, index = null) => {
    const cle = index === null ? message.id : `${message.id}-${index}`;
    setApplication(cle);
    try {
      await axiosInstance.post(
        `/ged/chat-sessions/${session}/messages/${message.id}/apply/`,
        index === null ? undefined : { proposal_index: index });
      setMessages((etat) => etat.map((m) => {
        if (m.id !== message.id) return m;
        if (index === null) return { ...m, applied: true };
        const propositions = (m.payload?.proposals || []).map(
          (p, i) => (i === index ? { ...p, applied: true } : p));
        return { ...m, payload: { ...m.payload, proposals: propositions } };
      }));
    } catch (e) {
      setErreur(e.response?.data?.error || "L'action n'a pas pu être appliquée.");
    } finally {
      setApplication(null);
    }
  };

  const actions = recettes.length > 0
    ? recettes.map((r) => ({ label: r.name, prompt: r.prompt, recetteId: r.id }))
    : quickActions;

  /**
   * Une action que l'agent propose : elle attend un clic, toujours, et
   * montre ce qu'elle écrira avant qu'on le donne.
   */
  const proposition = (message, contenu, index) => {
    const cle = index === null ? message.id : `${message.id}-${index}`;
    const faite = index === null
      ? message.applied
      : message.payload?.proposals?.[index]?.applied;
    return (
      <Proposition
        key={cle}
        proposition={contenu}
        faite={Boolean(faite)}
        enCours={application === cle}
        onConfirmer={() => appliquer(message, index)}
      />
    );
  };

  return (
    <Stack sx={{ height: hauteur }} spacing={1.5}>
      {actions.length > 0 && (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {actions.map((action, i) => (
            <Button key={i} size="small" variant="outlined"
                    onClick={() => envoyer(action.prompt, action.recetteId)}
                    disabled={envoi || !session}>
              {action.label}
            </Button>
          ))}
        </Stack>
      )}

      {erreur && <Alert severity="error"
                        onClose={() => setErreur('')}>{erreur}</Alert>}

      <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2, borderRadius: 1,
                 border: '1px solid', borderColor: 'divider',
                 bgcolor: 'background.default' }}>
        {messages.length === 0 && (
          <Typography variant="body2" color="text.secondary"
                      sx={{ textAlign: 'center', mt: 4 }}>
            {emptyHint}
          </Typography>
        )}

        {messages.map((message) => {
          const demande = message.role === 'user';
          const propositions = message.payload?.proposals;
          return (
            <Box key={message.id}
                 sx={{ display: 'flex', mb: 1.5,
                       justifyContent: demande ? 'flex-end' : 'flex-start' }}>
              {/* Une proposition porte un tableau : la lui serrer à 80 %
                  la rendrait illisible, alors que c'est ce qu'on doit lire
                  avant de valider. */}
              <Box sx={{ maxWidth: propositions?.length ? '95%' : '80%',
                         minWidth: propositions?.length ? '70%' : undefined }}>
                <Paper
                  variant={demande ? 'elevation' : 'outlined'}
                  elevation={0}
                  sx={{ p: 1.5, whiteSpace: 'pre-wrap',
                        bgcolor: demande ? 'primary.main' : 'background.paper',
                        color: demande ? 'primary.contrastText'
                                       : 'text.primary' }}
                >
                  <Typography variant="body2">{message.content}</Typography>
                </Paper>

                {!demande && propositions?.map((p, i) => proposition(
                  message, p, i))}

                {/* L'ancien format n'avait qu'une action par message : son
                    contenu est la charge utile elle-même. */}
                {!demande && message.action_type
                  && message.action_type !== 'proposals'
                  && message.payload && !propositions
                  && proposition(message, {
                    label: ACTIONS[message.action_type] || 'Données détectées',
                    data: message.payload,
                  }, null)}
              </Box>
            </Box>
          );
        })}
        <div ref={bas} />
      </Box>

      <Stack direction="row" spacing={1} alignItems="flex-end">
        <TextField
          placeholder={placeholder}
          value={saisie}
          onChange={(e) => setSaisie(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              envoyer(saisie);
            }
          }}
          disabled={envoi || !session}
          multiline
          maxRows={4}
        />
        <IconButton color="primary" onClick={() => envoyer(saisie)}
                    disabled={envoi || !saisie.trim() || !session}
                    aria-label="Envoyer">
          {envoi ? <CircularProgress size={20} /> : <BsSend />}
        </IconButton>
      </Stack>
    </Stack>
  );
}
